import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, Loader2, Check, X, Download, Copy, RefreshCw } from "lucide-react";

const getNextFriday = () => {
  const today = new Date();
  
  // Calcular próxima sexta-feira
  let nextFriday = new Date(today);
  
  // Se hoje for sexta, pegar a próxima semana
  if (today.getDay() === 5) {
    nextFriday.setDate(today.getDate() + 7);
  } else {
    // Calcular dias até sexta-feira (5)
    const daysToAdd = (5 - today.getDay() + 7) % 7 || 7;
    nextFriday.setDate(today.getDate() + daysToAdd);
  }
  
  // Formatar como YYYY-MM-DD
  const year = nextFriday.getFullYear();
  const month = String(nextFriday.getMonth() + 1).padStart(2, '0');
  const day = String(nextFriday.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const isFriday = (dateString: string) => {
  const date = new Date(dateString + 'T12:00:00');
  return date.getDay() === 5;
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

export const WeeklyGiraTab = () => {
  const [giraName, setGiraName] = useState("");
  const [giraDescription, setGiraDescription] = useState("");
  const [giraDate, setGiraDate] = useState(getNextFriday());
  const [giraTime, setGiraTime] = useState("19:30");
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [instagramText, setInstagramText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDateConfirmDialog, setShowDateConfirmDialog] = useState(false);
  const [pendingGeneration, setPendingGeneration] = useState(false);
  const imagePreviewRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const handleGenerateGira = async () => {
    if (!giraName.trim()) {
      toast({
        title: "Nome da gira necessário",
        description: "Por favor, preencha o nome da gira",
        variant: "destructive",
      });
      return;
    }

    if (!giraDate) {
      toast({
        title: "Data necessária",
        description: "Por favor, selecione a data da gira",
        variant: "destructive",
      });
      return;
    }

    // Validate if selected date is a Friday
    if (!isFriday(giraDate)) {
      setShowDateConfirmDialog(true);
      setPendingGeneration(true);
      return;
    }

    proceedWithGeneration();
  };

  const proceedWithGeneration = async () => {
    setIsGenerating(true);
    setPendingGeneration(false);
    setShowDateConfirmDialog(false);
    try {
      const { data, error } = await supabase.functions.invoke('generate-weekly-gira', {
        body: { 
          giraName: giraName.trim(),
          giraDescription: giraDescription.trim(),
          giraDate,
          giraTime
        }
      });

      if (error) throw error;

      setGeneratedImage(data.imageUrl);
      setInstagramText(data.instagramText || "");

      // Scroll to image preview after state updates
      setTimeout(() => {
        imagePreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      // Save to weekly_gira table
      await supabase
        .from('weekly_gira')
        .insert({
          gira_name: giraName.trim(),
          gira_description: giraDescription.trim() || null,
          image_url: data.imageUrl,
          instagram_text: data.instagramText || null,
          status: 'pending',
          week_start_date: giraDate
        });

      toast({
        title: "Arte gerada!",
        description: "Revise a imagem e aprove ou rejeite.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao gerar arte",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    setIsSaving(true);
    try {
      // Get the latest pending gira
      const { data: giraData } = await supabase
        .from('weekly_gira')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!giraData) throw new Error('Gira não encontrada');

      // Update status to approved
      await supabase
        .from('weekly_gira')
        .update({ status: 'approved' })
        .eq('id', giraData.id);

      // Update current_gira_images for gira_semana
      await supabase
        .from('current_gira_images')
        .update({ image_url: giraData.image_url })
        .eq('image_type', 'gira_semana');

      toast({
        title: "Arte aprovada!",
        description: "A imagem já está visível na página inicial.",
      });

      // Reset form
      setGiraName("");
      setGiraDescription("");
      setGiraDate(getNextFriday());
      setGiraTime("19:30");
      setGeneratedImage(null);
      setInstagramText("");
    } catch (error: any) {
      toast({
        title: "Erro ao aprovar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    try {
      // Get the latest pending gira
      const { data: giraData } = await supabase
        .from('weekly_gira')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (giraData) {
        await supabase
          .from('weekly_gira')
          .update({ status: 'rejected' })
          .eq('id', giraData.id);
      }

      setGeneratedImage(null);
      setInstagramText("");
      toast({
        title: "Arte rejeitada",
        description: "Você pode gerar uma nova versão.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao rejeitar",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRegenerate = async () => {
    try {
      // Get the latest pending gira and reject it
      const { data: giraData } = await supabase
        .from('weekly_gira')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (giraData) {
        await supabase
          .from('weekly_gira')
          .update({ status: 'rejected' })
          .eq('id', giraData.id);
      }

      // Clear current image
      setGeneratedImage(null);
      setInstagramText("");

      // Generate new one with same form data
      await handleGenerateGira();
    } catch (error: any) {
      toast({
        title: "Erro ao gerar novamente",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDownloadImage = async () => {
    if (!generatedImage) return;
    try {
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `gira-${giraName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);

      toast({
        title: "Download concluído",
        description: "A imagem foi salva com sucesso.",
      });
    } catch (error) {
      console.error('Erro no download da imagem:', error);
      window.open(generatedImage, '_blank');
      toast({
        title: "Download alternativo",
        description: "A imagem foi aberta em outra aba. Em celulares, pressione e segure para salvar.",
      });
    }
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(instagramText);
    toast({
      title: "Texto copiado!",
      description: "Cole no Instagram para publicar.",
    });
  };

  const handleCancelDate = () => {
    setShowDateConfirmDialog(false);
    setPendingGeneration(false);
    setGiraDate(getNextFriday());
  };

  return (
    <div className="space-y-6">
      <AlertDialog open={showDateConfirmDialog} onOpenChange={setShowDateConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Data não é uma sexta-feira</AlertDialogTitle>
            <AlertDialogDescription>
              A data selecionada ({formatDate(giraDate)}) não é uma sexta-feira. 
              As giras normalmente acontecem às sextas-feiras.
              Deseja continuar mesmo assim?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDate}>
              Cancelar e Corrigir
            </AlertDialogCancel>
            <AlertDialogAction onClick={proceedWithGeneration}>
              Sim, Continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card>
        <CardHeader>
          <CardTitle>Gira da Semana</CardTitle>
          <CardDescription>
            Crie a arte da gira da semana com IA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="gira-name">Nome da Gira *</Label>
            <Input
              id="gira-name"
              placeholder="Ex: Pretos-Velhos, Caboclos, Exú e Pombagira..."
              value={giraName}
              onChange={(e) => setGiraName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gira-description">Instruções para IA (Opcional)</Label>
            <Textarea
              id="gira-description"
              placeholder="Instruções específicas para personalizar a imagem gerada pela IA. Ex: 'Usar cores mais vibrantes', 'Incluir elemento de água', 'Estilo mais minimalista', 'Fundo com floresta densa'..."
              value={giraDescription}
              onChange={(e) => setGiraDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gira-date">Data da Gira *</Label>
              <Input
                id="gira-date"
                type="date"
                value={giraDate}
                onChange={(e) => setGiraDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gira-time">Horário</Label>
              <Input
                id="gira-time"
                type="time"
                value={giraTime}
                onChange={(e) => setGiraTime(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleGenerateGira}
            disabled={isGenerating || !giraName.trim() || !giraDate}
            className="w-full"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Gerando Arte com IA...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Gerar Arte da Gira
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {generatedImage && (
        <Card ref={imagePreviewRef}>
          <CardHeader>
            <CardTitle>Preview da Arte</CardTitle>
            <CardDescription>
              Revise a imagem gerada e aprove ou rejeite
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative w-full overflow-hidden rounded-lg border">
              <img
                src={generatedImage}
                alt="Arte da Gira"
                className="w-full h-auto"
              />
            </div>
            
            <Button
              onClick={handleDownloadImage}
              variant="outline"
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar Imagem (1080x1350)
            </Button>

            {instagramText && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Texto para Instagram</Label>
                  <Button
                    onClick={handleCopyText}
                    variant="ghost"
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copiar
                  </Button>
                </div>
                <Textarea
                  value={instagramText}
                  readOnly
                  rows={10}
                  className="font-mono text-sm"
                />
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                onClick={handleApprove}
                disabled={isSaving}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprovar e Publicar
              </Button>
              <Button
                onClick={handleRegenerate}
                disabled={isGenerating || isSaving}
                variant="outline"
                className="flex-1"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Gerar Novamente
              </Button>
              <Button
                onClick={handleReject}
                disabled={isSaving}
                variant="destructive"
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Rejeitar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};